import { useState, useCallback } from "react";
import { useAuth } from "@clerk/clerk-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useSchool } from "@/context/SchoolContext";
import { BACKEND_URL } from "@/constants/api";
import { useAbortableLoader, isAbortError } from "@/hooks/useAbortableLoader";
import { Button } from "@/components/ui/button";
import { PageContainer } from "@/layouts/PageContainer";
import { PaywallPrompt } from "@/components/ui/PaywallPrompt";
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from "@/components/ui/table";
import { ChevronLeft, AlertCircle, Trash2, BarChart2 } from "lucide-react";
import {
  ResponsiveContainer,
  LineChart, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid,
  BarChart, Bar,
  PieChart, Pie, Cell,
} from "recharts";

const BRAND = "hsl(152,55%,45%)";
const PRIMARY = "hsl(var(--primary))";
const PIE_COLORS = ["hsl(152,55%,45%)", "hsl(210,80%,55%)", "hsl(35,90%,55%)", "hsl(280,60%,55%)"];

function formatLastActive(dateStr) {
  if (!dateStr) return "Never";
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMin = Math.floor(diffMs / 60000);
  const diffHrs = Math.floor(diffMin / 60);
  const diffDays = Math.floor(diffHrs / 24);
  if (diffMin < 1) return "Just now";
  if (diffMin < 60) return `${diffMin} minute${diffMin !== 1 ? "s" : ""} ago`;
  if (diffHrs < 24) return `${diffHrs} hour${diffHrs !== 1 ? "s" : ""} ago`;
  if (diffDays < 30) return `${diffDays} day${diffDays !== 1 ? "s" : ""} ago`;
  return date.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

function formatDateLabel(dateStr) {
  const [, month, day] = dateStr.split("-");
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return `${months[parseInt(month, 10) - 1]} ${parseInt(day, 10)}`;
}

function StatCard({ label, value }) {
  return (
    <div className="bg-card border border-border rounded-xl px-5 py-4 flex flex-col gap-1">
      <span className="text-[0.72rem] font-semibold tracking-[0.07em] uppercase text-muted-foreground">{label}</span>
      <span className="font-display text-[2rem] leading-none text-foreground">{value ?? 0}</span>
    </div>
  );
}

function ChartCard({ title, children }) {
  return (
    <div className="bg-card border border-border rounded-2xl p-5">
      <p className="text-sm font-semibold text-foreground mb-4">{title}</p>
      {children}
    </div>
  );
}

export default function SchoolActivity() {
  const { memberships } = useSchool();
  const schoolId = memberships[0]?.school?._id;
  const schoolName = memberships[0]?.school?.name;
  const { getToken } = useAuth();

  const [analytics, setAnalytics] = useState(null);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paywalled, setPaywalled] = useState(false);
  const [actionInProgress, setActionInProgress] = useState(null);

  const load = useCallback(async (signal) => {
    try {
      setLoading(true);
      setError(null);
      setPaywalled(false);
      const token = await getToken();
      const headers = { Authorization: `Bearer ${token}` };

      const [analyticsRes, usageRes] = await Promise.all([
        fetch(`${BACKEND_URL}api/schools/${schoolId}/analytics`, { headers, signal }),
        fetch(`${BACKEND_URL}api/schools/${schoolId}/teacher-usage`, { headers, signal }),
      ]);

      if (analyticsRes.status === 402 || usageRes.status === 402) {
        setPaywalled(true);
        return;
      }
      if (!analyticsRes.ok || !usageRes.ok) throw new Error("Failed to load school activity");

      const [analyticsData, usageData] = await Promise.all([analyticsRes.json(), usageRes.json()]);
      setAnalytics(analyticsData);
      setTeachers(usageData.teachers ?? []);
    } catch (err) {
      if (isAbortError(err)) return;
      setError(err.message);
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [schoolId, getToken]);

  useAbortableLoader(load);

  const removeMember = async (userId, name) => {
    if (!confirm(`Remove ${name} from this school?`)) return;
    setActionInProgress(userId);
    try {
      const token = await getToken();
      const res = await fetch(`${BACKEND_URL}api/schools/${schoolId}/members/${userId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || "Failed to remove member");
      }
      toast.success("Member removed.");
      await load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setActionInProgress(null);
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <div className="h-8 w-40 bg-muted animate-pulse rounded mb-6" />
        <div className="grid grid-cols-5 gap-3 mb-6 max-[900px]:grid-cols-3 max-[560px]:grid-cols-2">
          {[0,1,2,3,4].map(i => <div key={i} className="h-20 bg-muted animate-pulse rounded-xl" />)}
        </div>
        <div className="h-64 bg-muted animate-pulse rounded-2xl mb-6" />
        <div className="grid grid-cols-2 gap-4 mb-6 max-[700px]:grid-cols-1">
          <div className="h-56 bg-muted animate-pulse rounded-2xl" />
          <div className="h-56 bg-muted animate-pulse rounded-2xl" />
        </div>
        <div className="h-40 bg-muted animate-pulse rounded-xl" />
      </PageContainer>
    );
  }

  if (paywalled) {
    return (
      <PageContainer>
        <Link to="/schools" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6">
          <ChevronLeft size={14} />
          {schoolName || "My School"}
        </Link>
        <PaywallPrompt message="School activity analytics requires an active School Plan." />
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <div className="flex items-center gap-2 text-destructive text-sm rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3">
          <AlertCircle size={15} />
          {error}
        </div>
      </PageContainer>
    );
  }

  const { stats, activityOverTime = [], actionBreakdown = [] } = analytics ?? {};
  const seatTeachers = teachers.filter(t => t.hasSeat);
  const pieData = actionBreakdown.filter(d => d.count > 0);

  return (
    <PageContainer>
      <Link to="/schools" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6">
        <ChevronLeft size={14} />
        {schoolName || "My School"}
      </Link>

      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
          <BarChart2 size={18} className="text-primary" />
        </div>
        <div>
          <h1 className="font-display text-2xl font-normal text-foreground">School Activity</h1>
          <p className="text-sm text-muted-foreground mt-1">Usage trends and teacher breakdown for your school.</p>
        </div>
      </div>

      <div className="grid grid-cols-5 gap-3 mb-6 max-[900px]:grid-cols-3 max-[560px]:grid-cols-2">
        <StatCard label="Total Reports" value={stats?.totalReports} />
        <StatCard label="AI Uses" value={stats?.totalAiUses} />
        <StatCard label="Report Folders Created" value={stats?.totalReportFolders} />
        <StatCard label="Report Folder Shares" value={stats?.totalShares} />
        <StatCard label="Active This Month" value={stats?.activeTeachersThisMonth} />
      </div>

      <ChartCard title="Reports & AI Uses — Last 30 Days">
        <ResponsiveContainer width="100%" height={220}>
          <LineChart data={activityOverTime} margin={{ top: 4, right: 16, left: -20, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
            <XAxis
              dataKey="date"
              tickFormatter={formatDateLabel}
              interval={6}
              tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
            />
            <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} allowDecimals={false} />
            <Tooltip
              labelFormatter={formatDateLabel}
              contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }}
            />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Line type="monotone" dataKey="reports" name="Reports" stroke={BRAND} dot={false} strokeWidth={2} />
            <Line type="monotone" dataKey="ai" name="AI Uses" stroke={PRIMARY} dot={false} strokeWidth={2} />
          </LineChart>
        </ResponsiveContainer>
      </ChartCard>

      <div className="grid grid-cols-2 gap-4 mt-4 max-[700px]:grid-cols-1">
        <ChartCard title="Per-Teacher Usage (Seat Data)">
          {seatTeachers.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">No seat holders yet.</p>
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={seatTeachers} margin={{ top: 4, right: 8, left: -24, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis
                  dataKey="name"
                  tickFormatter={n => n?.split(" ")[0] ?? n}
                  tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                />
                <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} allowDecimals={false} />
                <Tooltip
                  contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="reportsUsed" name="Reports Used" fill={BRAND} radius={[3,3,0,0]} />
                <Bar dataKey="aiGensUsed" name="AI Uses" fill={PRIMARY} radius={[3,3,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <ChartCard title="Activity Breakdown">
          {pieData.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">No activity recorded yet.</p>
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={pieData} dataKey="count" nameKey="label" cx="50%" cy="50%" outerRadius={70} innerRadius={36}>
                  {pieData.map((_, i) => (
                    <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
      </div>

      <div className="mt-6">
        {teachers.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 py-14 text-center rounded-2xl border border-dashed border-border bg-muted/20">
            <BarChart2 size={28} className="text-muted-foreground/40" />
            <p className="text-sm text-muted-foreground">No teachers in this school yet</p>
          </div>
        ) : (
          <div className="bg-card border border-border rounded-xl overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Teacher</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Report Folders</TableHead>
                  <TableHead>Reports</TableHead>
                  <TableHead>Last Active</TableHead>
                  <TableHead className="w-12" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {teachers.map((teacher) => {
                  const uid = teacher.userId?.toString?.() ?? teacher.userId;
                  const busy = actionInProgress === uid;
                  return (
                    <TableRow key={uid}>
                      <TableCell className="font-medium text-foreground">{teacher.name || "-"}</TableCell>
                      <TableCell className="text-muted-foreground">{teacher.email || "-"}</TableCell>
                      <TableCell>{teacher.reportFolderCount ?? 0}</TableCell>
                      <TableCell>{teacher.reportCount ?? 0}</TableCell>
                      <TableCell className="text-muted-foreground">{formatLastActive(teacher.lastActiveAt)}</TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                          title="Remove member"
                          disabled={busy}
                          onClick={() => removeMember(uid, teacher.name)}
                        >
                          <Trash2 size={14} />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </PageContainer>
  );
}
